// @ts-nocheck
import { DashboardLayout } from "./Dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Pill,
  Activity,
  Stethoscope,
  ArrowLeft,
  Info,
  Eye,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/use-auth";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useMemo, useState } from "react";
import TableWithSearch from "@/components/TableWithSearch";
import { careplanHeaders, treatmentPlanHeaders } from "@/utils/tablesData";
import { useNavigate } from "react-router";
import { formatDate } from "@/utils/helpers";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AxiosInstance from "@/utils/AxiosInstance";

interface Patient {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  conditions: string[];
  assignedProviderId?: string;
}

interface Provider {
  _id: string;
  name: string;
  specialty: string;
}

export default function PatientCarePlan() {
  const { user, patientEmail, profile } = useAuth();

  // ✅ Dummy patient and provider data (replace with API later)
  const [patients, setPatients] = useState<Patient[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [careplans, setCareplans] = useState([]);
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [treatmentPlanTabHasData, setTreatmentPlanTabHasData] = useState(false);
  const [treatmentPlanPrefetchDone, setTreatmentPlanPrefetchDone] =
    useState(false);
  const [activeTab, setActiveTab] = useState("care-plan");
  const [patientId, setPatientId] = useState<string | null>(null);
  const navigate = useNavigate();

  const hasRPM = useMemo(() => {
    const programs = profile?.programs;
    if (!Array.isArray(programs)) return false;
    return programs.some(
      (program: string) => String(program).trim().toLowerCase() === "rpm",
    );
  }, [profile?.programs]);

  const showTreatmentPlanTab = hasRPM || treatmentPlanTabHasData;

  useEffect(() => {
    // 🧠 Later: Replace this with your Express API calls
    // Example:
    // const res = await axiosInstance.get("/patients");
    // setPatients(res.data);

    // Temporary dummy data
    setTimeout(() => {
      setPatients([
        {
          _id: "1",
          firstName: "John",
          lastName: "Doe",
          email: "john.doe@example.com",
          dateOfBirth: "1980-05-12",
          conditions: ["Diabetes", "Hypertension"],
          assignedProviderId: "p1",
        },
        {
          _id: "2",
          firstName: "John",
          lastName: "Doe",
          email: "tempmail@temp.com",
          dateOfBirth: "1980-05-12",
          conditions: ["Diabetes", "Hypertension"],
          assignedProviderId: "p1",
        },
      ]);
      setProviders([
        {
          _id: "p1",
          name: "Dr. Sarah Williams",
          specialty: "Internal Medicine",
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  // Prefetch treatment plans so the tab can show when data exists (even without RPM).
  useEffect(() => {
    if (hasRPM) {
      setTreatmentPlanPrefetchDone(true);
      return;
    }

    const resolvedPatientId = patientId || profile?._id || null;
    // Wait for auth profile so we can send patientId when available.
    if (!resolvedPatientId && profile == null) return;

    let cancelled = false;
    setTreatmentPlanPrefetchDone(false);

    (async () => {
      try {
        const result = await AxiosInstance().get("/treatment-plan", {
          params: resolvedPatientId ? { patientId: resolvedPatientId } : {},
        });
        if (cancelled) return;

        const plans = result?.data?.data?.treatmentPlan;
        if (Array.isArray(plans) && plans.length > 0) {
          setTreatmentPlanTabHasData(true);
          setTreatmentPlans(plans);
        }
      } finally {
        if (!cancelled) setTreatmentPlanPrefetchDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasRPM, patientId, profile, profile?._id]);

  // Get patient ID from first care plan or treatment plan
  useEffect(() => {
    if (careplans.length > 0 && careplans[0]?.patientId?._id) {
      setPatientId(careplans[0].patientId._id);
    } else if (treatmentPlans.length > 0 && treatmentPlans[0]?.patientId?._id) {
      setPatientId(treatmentPlans[0].patientId._id);
    } else if (profile?._id) {
      setPatientId(profile._id);
    }
  }, [careplans, treatmentPlans, profile?._id]);

  // Fall back if treatment-plan tab is selected but should not be shown.
  useEffect(() => {
    if (activeTab !== "treatment-plan") return;
    if (showTreatmentPlanTab) return;
    if (!treatmentPlanPrefetchDone && !hasRPM) return;
    setActiveTab("care-plan");
  }, [
    activeTab,
    showTreatmentPlanTab,
    treatmentPlanPrefetchDone,
    hasRPM,
  ]);

  const currentPatient = patients.find(
    (p) =>
      p.email ===
      (patientEmail || " "),
      // || user?.email
  );

  const assignedProvider = providers.find(
    (p) => p._id === currentPatient?.assignedProviderId,
  );

  if (loading) {
    return (
      <DashboardLayout activePage="/patient-careplan">
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground">Loading your care plan...</p>
        </div>
      </DashboardLayout>
    );
  }

  // if (!currentPatient) {
  //   return (
  //     <DashboardLayout activePage="/patient-careplan">
  //       <div className="flex items-center justify-center min-h-[60vh]">
  //         <p className="text-muted-foreground">No care plan found for your account.</p>
  //       </div>
  //     </DashboardLayout>
  //   );
  // }

  return (
    <DashboardLayout activePage="/patient-careplan">
      <div className="space-y-6">
        {/* <div className="flex items-center justify-end">
          <Button
            variant="outline"
            onClick={() => navigate("/patient-manage-access")}
          >
            Manage Access
          </Button>
        </div> */}
        {/* <div>
          <h2 className="text-3xl font-bold tracking-tight">My Plan</h2>
          <p className="text-muted-foreground mt-2">
            Your comprehensive chronic care management plan
          </p>
        </div> */}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-10 w-full md:w-fit rounded-lg bg-grey-light border border-grey-light p-0 overflow-hidden">
            <TabsTrigger
              value="care-plan"
              className="teachers text-sm px-4 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
            >
              My Care Plan
            </TabsTrigger>
            {showTreatmentPlanTab && (
              <TabsTrigger
                value="treatment-plan"
                className="teachers text-sm px-4 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none rounded-none"
              >
                My Treatment Plan
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="care-plan" className="mt-2 md:mt-4 border p-4 rounded-xl">
            <TableWithSearch
              apiDataKey="carePlans"
              isColumnSelection
              columnPreferenceKey={`my-careplan-tab-${patientId || "portal"}`}
              clickRow={() => {}}
              columnHeaders={careplanHeaders}
              data={careplans}
              viewData={careplans.map((v: any) => {
                const careTakerName = [v?.careTakerId?.firstName, v?.careTakerId?.middleName, v?.careTakerId?.lastName].filter(Boolean).join(" ");
                const patientName = [v?.patientId?.firstName, v?.patientId?.middleName, v?.patientId?.lastName].filter(Boolean).join(" ");
                const planType = v?.carePlanType || v?.plan || v?.planType || v?.type || "";
                return {
                  ...v,
                  plan: planType ? String(planType).toUpperCase() : "—",
                  patientId: patientName,
                  careTakerId: careTakerName,
                  createdAt: v?.createdAt ? formatDate(v.createdAt) : "",
                  updatedAt: v?.updatedAt ? formatDate(v.updatedAt) : "",
                };
              })}
              setData={setCareplans}
              fetchCondition={activeTab === "care-plan"}
              isIncludeSearch
              searchPlaceholder="Search Careplans..."
              filters={[]}
              filtersOptions={[]}
              setFilters={() => {}}
              heading="Careplans List"
              isActionColumn={false}
              handleAction={() => {}}
              moreActions={[
                {
                  label: "View",
                  icon: <FileText className="w-4 h-4" />,
                  onClick: (data) =>
                    navigate(`/patient-careplan/${data?._id}`, {
                      state: { patientData: {}, carePlan: data },
                    }),
                  isShow: () => true,
                },
              ]}
              params={{}}
              url="/patients/careplan"
            />
          </TabsContent>

          {showTreatmentPlanTab && (
            <TabsContent value="treatment-plan" className="mt-2 md:mt-4 border p-4 rounded-xl">
              <TableWithSearch
                apiDataKey="treatmentPlan"
                isColumnSelection
                columnPreferenceKey={`my-treatment-plan-tab-${patientId || "portal"}`}
                clickRow={() => {}}
                columnHeaders={treatmentPlanHeaders}
                data={treatmentPlans}
                viewData={(treatmentPlans || []).map((v: any) => 
                  {
                  const carTakerName = [v?.careTakerId?.firstName, v?.careTakerId?.middleName, v?.careTakerId?.lastName].filter(Boolean).join(" ");
                  const patientName = [v?.patientId?.firstName, v?.patientId?.middleName, v?.patientId?.lastName].filter(Boolean).join(" ");
                  return ({
                  ...v,
                  patientId: patientName,
                  careTakerId: carTakerName,
                  deviceType: Array.isArray(v?.deviceType) 
                    ? v.deviceType.map((dt: string) => dt.replace(/_/g, " ")).join(", ") 
                    : v?.deviceType?.replace(/_/g, " ") || "N/A",
                  status: v?.status?.replace(/_/g, " ") || "",
                  createdAt: v?.createdAt ? formatDate(v.createdAt) : "",
                  updatedAt: v?.updatedAt ? formatDate(v.updatedAt) : "",
                });
                })}
                setData={setTreatmentPlans}
                fetchCondition={activeTab === "treatment-plan"}
                isIncludeSearch
                searchPlaceholder="Search Treatment Plans..."
                filters={[]}
                filtersOptions={[]}
                setFilters={() => {}}
                heading="Treatment Plans List"
                isActionColumn={false}
                handleAction={() => {}}
                moreActions={[
                  {
                    label: "View",
                    icon: <Eye className="w-4 h-4" />,
                    onClick: (data) => {
                      if (patientId) {
                        navigate(`/treatment-plan/${patientId}/${data?._id}?view=true`, {
                          state: { patientData: {}, carePlan: data },
                        });
                      }
                    },
                    isShow: () => true,
                  },
                ]}
                params={patientId ? { patientId } : {}}
                url="/treatment-plan"
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function CCMSection({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary-foreground" />
        </div>
        <span className="font-medium">{title}</span>
      </div>
      <ArrowLeft className="w-5 h-5 text-muted-foreground rotate-180 group-hover:translate-x-1 transition-transform" />
    </motion.div>
  );
}
